import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch, useHistory } from "react-router-dom";
import Truncate from "react-truncate";

import {
  Row,
  Col,
  Form,
  Button,
  Dropdown,
  Image
} from "react-bootstrap";

import _ from 'lodash';
import cx from 'classnames';

import InfiniteScroll from "components/InfiniteScroll";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { TRIGGER_TYPES, LANDING_PAGE_TRIGGER, ASSET_MAPPING_TRIGGER, SPATIAL_TRIGGER, FLIP_BOOK_TRIGGER } from 'constants/gatingTriggerTypes';
import { PAGE } from "constants/widgetTypes";
import { getBaseUrl } from 'services/util';

import { fetchPublishedGatings } from "actions/gating";
import { fetchSlidesForPage } from "actions/pages";
import storyboardRoleService from "services/storyboardRoles.service";

import smartGatingIcon from "../../assets/images/smartGating.svg";
import { toast } from "react-toastify";
import { FailureToast } from "components/Toast";
import { INTERACTIVE_CONTENT_TYPES, SLIDE_STORYBOARDS, STORYBOARD_TYPES } from "constants/storyboardType";
import styles from './gatings.module.scss';

const GatingConfigurations = ({ index, gatingLength, gatingConfig, storyboard, type, onUpdate, onDelete, className, isSmartEnabled }) => {
  const landingPages = useSelector(state => _.get(state, 'pages.items', [])) || [];
  const smart = _.get(gatingConfig, 'smart');
  const target = _.get(gatingConfig, 'target', []);
  const slidesByPage = useSelector((state) => _.get(state, `pages.spatialSlides`, []));
  const currentStoryboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  const dispatch = useDispatch();

  const handleUpdate = (id, request) => {
    onUpdate(id, request);
  }

  const handleChange = (id, request) => {
    onUpdate(id, request);
    if( SLIDE_STORYBOARDS.includes(storyboard.type) && request.target) {
      handleFetchSlides(request.target.id, 0);
    }
  }

  useEffect(() => { 
    handleFetchSlides(target?.id, 0);
  },[])

  useEffect(() => {
    dispatch(fetchPublishedGatings({
      storyboard: storyboard.id,
      published: true
    }, 0, 100));
  }, []);

  const handleFetchSlides = (id, page) => {
    if (!slidesByPage?.[id]) {
      dispatch(fetchSlidesForPage(id, page, 50));
    }
  }

  const toggleOptionalGating = () => {
    handleUpdate(gatingConfig.uid, { optional: !gatingConfig.optional});
  }

  return (
    <div key={index} className={cx(styles.gatingContent, {[styles.disabled] : !storyboardRoleService.canEditStoryboard(currentStoryboard)}, className)}>
      {/* Gating Header */}
      <GatingHeader 
        gatingConfig={gatingConfig} 
        onChange={(id, request) => handleUpdate(id, request)} 
        onDelete={onDelete}
        type={type} 
        isSmartEnabled={isSmartEnabled}
        order={gatingLength - index} 
      />

      {/* Gating Details Container */}
      <div className={cx(styles.detailsContainer, { [styles.assetMapping]: (type === "asset") })}>

        {/* Gating Screen Selection*/}
        <GatingScreen gatingConfig={gatingConfig} onChange={(id, request) => handleUpdate(id, request)} type={type} />

        {/* Page Selection */}
        {(!INTERACTIVE_CONTENT_TYPES.includes(storyboard.type) && type === 'page') &&
          <LandingPageSection gatingConfig={gatingConfig} landingPages={landingPages} onChange={(id, request) => handleChange(id, request)} storyboardType={storyboard.type}/>
        }

        {/* Trigger Selection */}
        <GatingTrigger pages={landingPages} isSmartEnabled={isSmartEnabled} gatingConfig={gatingConfig} storyboardType={storyboard.type} onChange={(id, request) => handleUpdate(id, request)} type={type} handleFetchSlides={handleFetchSlides}/>

        {/* Optional Gating */}
        <div style={{ minWidth: '71px', width: type === "asset" ? '100%' : 'auto' }}>
          {!smart &&
            <Form.Check
              custom
              type="checkbox"
              label='Optional'
              onChange={toggleOptionalGating}
              checked={gatingConfig.optional}
              className={cx(styles.optionalCheck, { [styles.assetMappingLabel]: (type === "asset") }, styles.activeColor)}
            />
          }
        </div>
      </div>

      {/* Progressive Gating Info */}
      {gatingConfig?.gating?.progressive && type === 'page' && !smart &&
        <div className={styles.progressiveInfo}>
          <span>
            {`Progressive Gating is enabled on the "`} 
            <TruncatedTextWithTooltip content={gatingConfig?.gating?.name || ""} maxWidth={300} />
            {`" screen.`}
          </span>
        </div>
      }
    </div>
  )
}

const LandingPageSection = React.memo(({ gatingConfig, landingPages, onChange, storyboardType }) => {
  const selected = _.get(gatingConfig, 'target', null);
  const smart = _.get(gatingConfig, 'smart', false);

  useEffect(() => {
    if(smart) {
      if(SLIDE_STORYBOARDS.includes(storyboardType)) {
        const slide = _.head(landingPages);
        handleChangePage(slide);
      } else {
        handleChangePage();
      }
    }
  }, [smart, landingPages]);

  useEffect(()=>{
    if(SLIDE_STORYBOARDS.includes(storyboardType) && !selected) {
      const slide = _.head(landingPages);
      handleChangePage(slide);
    }
  },[storyboardType, landingPages, smart])

  const handleChangePage = (page) => {
    if(page) {
      if(smart) {
        onChange(gatingConfig.uid, { target: null });
      } else {
        onChange(gatingConfig.uid, { target: { id: page.id, name: page.name, type: page.entityType } });
      }
      return;
    } 
    
    onChange(gatingConfig.uid, ({target: null}));
  }

  return (
    <div className={styles.pageSelectionContainer}>
      <span className={styles.label}>Page</span>
      <Dropdown className={cx(styles.pagesOptionDropdown, { [styles.itemDisabled]: smart })}>
        <Dropdown.Toggle>
          {selected ?
            <span className={styles.name}>
              <Truncate lines={1}>
                {_.get(selected, 'name', 'All Pages')}
              </Truncate>
            </span> :
            (SLIDE_STORYBOARDS.includes(storyboardType) && smart) ? 
            <span className={styles.name}>{landingPages?.[0]?.name}</span> :
            <span className={styles.name}>All Pages</span>
          }
        </Dropdown.Toggle>
        <Dropdown.Menu className="min-width-0" style={{ maxHeight: "200px" }}>
          { !SLIDE_STORYBOARDS.includes(storyboardType) &&
            <Dropdown.Item active={selected === null} onClick={() => handleChangePage()}>
              <span className={styles.name}>All Pages</span>
            </Dropdown.Item>
          }
          {landingPages.map((page, index) => {
            const active = selected && selected.id === page.id;
            return (
              <Dropdown.Item active={active} onClick={() => handleChangePage(page)} key={index}>
                <span className={styles.name}>
                  <Truncate lines={1}>
                    {page.name}
                  </Truncate>
                </span>
              </Dropdown.Item>
            )
          })
        }
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )
})

const GatingHeader = ({ order, gatingConfig, onChange, onDelete, type, isSmartEnabled }) => {
  const inputRef = React.useRef(null);

  const [editTitle, setEditTitle] = useState(false);
  const [name, setName] = useState(_.get(gatingConfig, "name", "Untitled Gating") || "Untitled Gating");

  const toggleEditClick = () => {
    setEditTitle(!editTitle);
  }

  useEffect(() => {
    if (editTitle) {
      inputRef.current.focus();
    }
  }, [editTitle])

  const handleNameChange = (e) => {
    setName(e.target.value);
  }

  const updateGatingName = (e) => {
    if (!name) {
      return;
    }

    var request = {
      name: name
    }


    onChange(gatingConfig.uid, request);
    setEditTitle(false);
  }
  const toggleEnableGating = () => {
    if(!gatingConfig.enabled && isSmartEnabled && gatingConfig.smart) {
      return toast(
        <FailureToast
          heading={`Failure`}
          description={'Auto Trigger is already active on another gating.'}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
    }

    onChange(gatingConfig.uid, { enabled: !gatingConfig.enabled });
  }

  const handleDeleteGating = () => {
    onDelete(gatingConfig.uid, gatingConfig.id);
  }

  const renderTitle = () => {
    if (editTitle) {
      return (
        <input
          ref={inputRef}
          type={'text'}
          className={styles.inputField}
          value={name}
          onChange={handleNameChange}
          onBlur={() => updateGatingName()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              updateGatingName();
            }
          }}
        />
      );
    } else {
      return (
        <>
          {type === "asset" && (
            <div className={styles.titleContainer}>
              <div className={styles.titleName}>
                {order.toString().padStart(2, "0")}
              </div>
            </div>
          )}
          <TruncatedTextWithTooltip
            content={gatingConfig.name}
            onClick={toggleEditClick}
            className={styles.title}
            placement="auto"
            maxWidth={300}
          />
        </>
      );
    }
  }
  return (
    <div className={styles.gatingHeader}>
      <div className={cx(styles.gatingName)}>
        {renderTitle()}
        {gatingConfig.enabled && type == "page" && <label className={cx(styles.status, styles.live)}>ACTIVE</label>}
      </div>
      <div className={styles.gatingAction}>
        <Button variant="info" onClick={handleDeleteGating} className={styles.deleteButton}><i className={cx('cleverstory-trash', styles.deleteIcon)} /></Button>
        <Form.Check
          type="switch"
          id="custom-switch"
          onChange={toggleEnableGating}
          checked={gatingConfig.enabled}
          className={styles.toggleSwitch}
        />
      </div>
    </div>
  )
}

const GatingTrigger = ({ isSmartEnabled, gatingConfig, storyboardType, onChange, type, handleFetchSlides, pages }) => {
  const page = useSelector((state) => _.get(state, "storyboards.storyboard.item.page", {}));
  const target = _.get(gatingConfig, 'target', []);
  const slideItems = useSelector((state) => _.get(state, `pages.spatialSlides.${target?.id}.items`, []));

  const pageSection = useSelector((state) => {
    const pageItem = _.get(state, `pages.page.${page.id}.item`, {}) || {};
    var pages = _.get(pageItem, 'draft.widgets', []).filter((item) => item.type === PAGE);
    pages = _.sortBy(pages, "order");
    return pages;
  });

  let triggerOptions = TRIGGER_TYPES;

  if (!INTERACTIVE_CONTENT_TYPES.includes(storyboardType)) {
    triggerOptions = LANDING_PAGE_TRIGGER;
  }

  if (storyboardType === 'SPATIAL') {
    triggerOptions = SPATIAL_TRIGGER;
  }

  if (storyboardType === 'FLIP_BOOK') {
    triggerOptions = FLIP_BOOK_TRIGGER;
  }

  if (type === "asset") {
    triggerOptions = ASSET_MAPPING_TRIGGER;
  }

  let trigger = _.get(gatingConfig, 'trigger') || {};
  if (storyboardType === 'FLIP_BOOK' && trigger.type === 'FLIPBOOK_SLIDE') {
    trigger = { ...trigger, type: 'SPATIAL_SLIDE' };
  }
  const smart = _.get(gatingConfig, 'smart');
  const autoGating = _.get(gatingConfig, 'autoGating');
  const progressive = _.get(gatingConfig, 'gating.progressive');

  if (smart) {
    trigger = {
      ...trigger,
      type: 'SMART'
    }
  }

  if (autoGating) {
    trigger = {
      ...trigger,
      type: 'AUTO_GATING'
    }
  }
  
  useEffect(() => {
    const t = _.get(gatingConfig, 'trigger');
    if (storyboardType !== 'FLIP_BOOK' || t?.type !== 'FLIPBOOK_SLIDE') return;
    onChange(gatingConfig.uid, {
      trigger: { ...t, type: 'SPATIAL_SLIDE' },
      smart: false,
    });
  }, [storyboardType, gatingConfig.uid, gatingConfig.trigger?.type]);

  useEffect(() => {
    if (!SLIDE_STORYBOARDS.includes(storyboardType) || trigger.type !== 'SPATIAL_SLIDE' || _.isEmpty(slideItems)) {
      return;
    }
    const nextValue = slideItems.some((item) => item.uid === trigger.value)
      ? trigger.value
      : _.head(slideItems)?.uid;
    if (nextValue === trigger.value) {
      return;
    }
    onChange(gatingConfig.uid, {
      trigger: {
        ...trigger,
        type: 'SPATIAL_SLIDE',
        value: nextValue,
      },
    });
  }, [slideItems, storyboardType, trigger.type, trigger.value, gatingConfig.uid]);

  const renderTriggerLabel = () => {
    var option = triggerOptions.find(item => item.type === trigger.type);
    return option?.label || "";
  }

  const handleChangeTrigger = (key, value) => {
    if(isSmartEnabled && key === 'type' && value === 'SMART' && gatingConfig.enabled) {
      return toast(
        <FailureToast
          heading={`Failure`}
          description={'Auto Trigger is already active on another gating.'}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
    }

    let request = {
      trigger: {
        ...trigger,
        [key]: value,
      },
      smart: false
    }

    if (key === 'type' && value === 'SMART'){
      request = {
        trigger : null,
        smart: true,
        optional: true
      }
    }

    if (key === 'type' && value === 'AUTO_GATING'){
      request = {
        trigger : null,
        smart: false,
        autoGating: true,
        optional: true
      }
    }
  
    if (key === 'type' && value === 'TIME') {
      request = {
        trigger: {
          type: 'TIME',
          value: 10
        },
        smart: false
      };
    }
  
    if (key === 'type' && value === 'PERCENTAGE') {
      request = {
        trigger: {
          type: 'PERCENTAGE',
          value: 10
        },
        smart: false
      };
    }
  
    if (key === 'type' && value === 'SPATIAL_SLIDE') {
      request = {
        trigger: {
          ...trigger,
          [key]: value,
          value: slideItems.some((item) => item.uid === trigger.value)
            ? trigger.value
            : _.head(slideItems)?.uid,
        },
        smart: false,
      };
    }

    if (key === 'type' && value === 'INTERACTIVE_SECTION') {
      const conditionalValue = trigger.type === 'INTERACTIVE_SECTION' ? trigger.value : 1;
      request = {
        trigger: {
          ...request.trigger,
          value: conditionalValue
        },
        smart: false
      };
    }

    onChange(gatingConfig.uid, request);
  }


  const SectionDropDown = ({ storyboardType }) => {
    return (
      <Dropdown className={cx(styles.triggerOptionDropdown, styles.selectionMenu)}>
        <Dropdown.Toggle>
          {trigger.type === 'INTERACTIVE_SECTION' && trigger.value ?
            <span className={styles.name}>
              <Truncate lines={1}>
                {trigger.value}
              </Truncate>
            </span> :
            <span className={styles.name}>Select Section</span>
          }
        </Dropdown.Toggle>
        <Dropdown.Menu className="min-width-0">
          {
            (storyboardType === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2 ? pages : pageSection).map((section, index) => {
              return (
                <Dropdown.Item onClick={() => handleChangeTrigger('value', index + 1)} key={index}>
                  <span className={styles.name}>{index + 1}</span>
                </Dropdown.Item>
              )
            })
          }
        </Dropdown.Menu>
      </Dropdown>
    )
  }

  const SlideTriggerDropDown = () => {
    const targetId = target?.id;
    const loading = useSelector((state) => _.get(state, `pages.spatialSlides.${targetId}.loading`, false));
    const pagination = useSelector((state) => _.get(state, `pages.spatialSlides.${targetId}.pagination`, {}));
    const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

    const loadMore = () => {
      const nextPage = pagination.currentPage + 1;
      if (nextPage >= pagination.totalPages) {
        return;
      }
      handleFetchSlides(targetId, nextPage);
    };

    return (
      <Dropdown className={cx(styles.triggerOptionDropdown, styles.slideMenu)}>
        <Dropdown.Toggle>
          <TruncatedTextWithTooltip
            className={cx(styles.name)}
            content={
              trigger.value
                ? slideItems.find((slide) => slide.uid === trigger.value)?.name || 'Loading...'
                : 'Loading...'
            }
            maxWidth={100}
            placement="top"
          />
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ maxHeight: "200px" }} className="min-width-0">
          <InfiniteScroll
            className={cx(styles.scrollable)}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={loadMore}
          >
            {slideItems.map((slide, index) => (
              <Dropdown.Item onClick={() => handleChangeTrigger('value', slide.uid)} key={index} style={{ width: "100%" }}>
                <TruncatedTextWithTooltip className={cx(styles.name)} content={slide.name} maxWidth={100} placement="top" />
              </Dropdown.Item>
            ))}
          </InfiniteScroll>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <div className={cx(styles.triggerContainer)}>
      <div className={cx(styles.pageGatingContainer, { [styles.assetMappingContainer]: type === "asset" })}>
        <span className={styles.label}>{(trigger.type === 'TIME' || trigger.type === 'PERCENTAGE') ? 'Trigger after' : 'Trigger at'}</span>
        <Dropdown className={cx(styles.triggerOptionDropdown)}>
          <Dropdown.Toggle>
            {renderTriggerLabel()}
          </Dropdown.Toggle>

          <Dropdown.Menu className="min-width-0" style={{ maxHeight: "200px", width: "auto" }}>
            {triggerOptions.map((option, index) => {
              const active = option.type === trigger.type;

              if(option.type === 'SMART' && progressive) {
                return null;
              }

              return (
                <Dropdown.Item className={cx({[styles.smartOption]: option.type === 'SMART'})} active={active} onClick={() => handleChangeTrigger('type', option.type)} key={index}>
                  <span className={styles.name}>
                    {option.type === 'SMART' && 
                      <div className={styles.smartGatingIconSection}>
                        <Image
                          className={styles.pulseImage}
                          src={smartGatingIcon}
                          width={15}
                          height={15}
                        />
                        <Image
                          className={styles.pulseImage2}
                          src={smartGatingIcon}
                          width={15}
                          height={15}
                        />
                      </div>
                    }
                    {option.label}
                  </span>
                </Dropdown.Item>
              )
            })
            }
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {trigger.type === 'TIME' &&
        <Form.Group className={styles.triggerInputWrap}>
          {type == "asset" && <div className={styles.triggerValueLabel}>{renderTriggerLabel()}</div>}
          <Form.Control key="timer" className={styles.triggerInput}
            onChange={(e) => {
              let value = parseInt(e.target.value);
              if(value <= 0 || isNaN(value)) {
                value = 0;
              } else if (value > 120) {
                value = 120;
              }
              handleChangeTrigger('value', parseInt(value))
            }}
            min={1} max={120}
            value={(typeof(trigger.value) == 'number') ? trigger.value : 10} type="number" 
          />
          <span>S</span>
          <Form.Control.Feedback type="invalid">
            Please provide a valid state.
          </Form.Control.Feedback>
        </Form.Group>
      }

      {(trigger.type === 'PERCENTAGE') &&
        <Form.Group as={Col} md="3" className={styles.triggerInputWrap}>
          {type == "asset" && <div className={styles.triggerValueLabel}>{renderTriggerLabel()}</div>}
          <Form.Control key="timer" className={styles.triggerInput}
            onChange={(e) => {
              let value = parseInt(e.target.value);
              if(value <= 0) {
                value = 0;
              } else if (value > 100) {
                value = 100;
              }
              handleChangeTrigger('value', parseInt(value))
            }}
            min={1} max={100}
            value={trigger.value > 100 ? 100 : (typeof(trigger.value) == 'number') ? trigger.value : 10} type="number" />
          <span>%</span>
          <Form.Control.Feedback type="invalid">
            Please provide a valid state.
          </Form.Control.Feedback>
        </Form.Group>
      }

      {trigger.type === 'INTERACTIVE_SECTION' &&
        <SectionDropDown storyboardType={storyboardType} />
      }

      {trigger.type === 'SPATIAL_SLIDE' && <SlideTriggerDropDown />}
    </div>
  )
}

const GatingScreen = ({ gatingConfig, onChange, type }) => {
  const dispatch = useDispatch();

  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));

  const gatings = useSelector(state => _.get(state, 'gatings.published.items', []));
  const loading = useSelector(state => _.get(state, 'gatings.published.loading', false));
  const pagination = useSelector(state => _.get(state, 'gatings.published.pagination', {}));
  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

  const gatingScreen = _.get(gatingConfig, 'gating');
  const smart = _.get(gatingConfig, 'smart');

  const [selectedGating, setSelectedGating] = useState();

  useEffect(() => {
    if(smart && gatingScreen?.progressive) {
      onChange(gatingConfig?.uid, { gating: null });
    }
  }, [smart, gatingScreen?.progressive, gatingConfig?.uid]);

  useEffect(() => {
    if(!gatingScreen) return;

    setSelectedGating(gatingScreen.name);
  }, [gatingScreen?.name]);

  const handleFetchMappings = (page) => {
    dispatch(fetchPublishedGatings({
      storyboard: storyboard.id,
      published: true
    }, page, 100));
  }

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleFetchMappings(nextPage);
  }

  const handleGatingScreen = (data) => {
    var selected = gatings.find(item => item.id === data);
    if (selected) {
      onChange(gatingConfig.uid, { gating: selected });
    }
  }

  const handleEditGating = (e) => {
    e.stopPropagation();

    if (!gatingScreen) {
      return;
    }

    const win = window.open(`${baseUrl}/gating/${gatingScreen.id}`, "_blank");
    win.focus();
  }

  const renderScreenOption = (selectedData, active) => {
    return (
      <span className={styles.optionName}>
        <Truncate lines={2}>
          {selectedData}
        </Truncate>
      </span>
    )
  }

  return (
    <div className={cx(styles.gatingScreenContainer, { [styles.flex]: (type === "page") })}>
      <span className={styles.label}>Gating Screen</span>

      <div className={styles.dropdownContainer}>
        <Dropdown className={cx(styles.gatingDropdown, { [styles.fullWidth]: (type === "asset") })}>
          <Dropdown.Toggle>
            {gatingScreen ?
              <span className={cx(styles.selectedName, {[styles.selectedNameProgressive]: gatingConfig?.gating?.progressive && type === 'page' && !smart })}>
                <TruncatedTextWithTooltip maxWidth={130} content={selectedGating} placement="top" />
              </span> :
              <span className={styles.selectedName}>Select Gating</span>
            }
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <InfiniteScroll
              className={cx(styles.scrollable)}
              loading={loading}
              hasMore={hasMoreItems}
              loadMore={loadMore}
            >
              <Dropdown.Item>
                No Options
              </Dropdown.Item>
              {
                gatings.map((data, index) => {
                  const activeGatingScreen = gatingScreen && gatingScreen.id === data.id;

                  if((data.progressive && smart) || (data.progressive && type !== 'page')) return null;

                  return (
                    <Dropdown.Item active={activeGatingScreen} onClick={() => handleGatingScreen(data.id)} key={index}>
                      {renderScreenOption(data.name, activeGatingScreen)}
                    </Dropdown.Item>
                  )
                })
              }
            </InfiniteScroll>
          </Dropdown.Menu>
        </Dropdown>
        { _.size(gatings) > 0 && <Button className={styles.gatingEditButton} variant="info" onClick={handleEditGating}>
          <i className={cx('cleverstory-edit', styles.editIcon)} />
        </Button>}

      </div>
    </div>
  )
}

export default GatingConfigurations